import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import TravelodgesLogo from './components/TravelodgesLogo';
import '@/App.css';

// Import pages
import HomePage from './pages/HomePage';
import HowItWorksPage from './pages/HowItWorksPage';
import ForResortsPage from './pages/ForResortsPage';
import PropertiesPage from './pages/PropertiesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import JoinPage from './pages/JoinPage';
import PaymentInstructionsPage from './pages/PaymentInstructionsPage';
import DashboardPage from './pages/DashboardPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';

// Navigation component
function Navigation({ isLoggedIn, currentUser, onLogin, onLogout, onPlanChange }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showPlanSelector, setShowPlanSelector] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/for-resorts', label: 'For Resorts' },
    { to: '/properties', label: 'Properties' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/about', label: 'About' }
  ];

  if (isLoggedIn) {
    navLinks.push({ to: '/dashboard', label: 'Dashboard' });
  }

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all ${
      scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`} data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="hover:opacity-90 transition-opacity">
            <TravelodgesLogo variant="small" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-gray-700 hover:text-emerald-600 transition-colors font-medium ${
                  location.pathname === link.to ? 'text-emerald-600' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button or User Menu */}
          <div className="hidden lg:flex items-center space-x-4">
            {isLoggedIn ? (
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowPlanSelector(!showPlanSelector)}
                  className="border-emerald-600 text-emerald-600"
                >
                  {currentUser?.subscriptionPlan} Plan
                </Button>
                {showPlanSelector && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                    <div className="py-2">
                      <button onClick={() => { onPlanChange('Basic'); setShowPlanSelector(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">Switch to Basic</button>
                      <button onClick={() => { onPlanChange('Pro'); setShowPlanSelector(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">Switch to Pro</button>
                      <button onClick={() => { onPlanChange('Premium'); setShowPlanSelector(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">Switch to Premium</button>
                      <hr className="my-2" />
                      <button onClick={onLogout} className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600">Logout</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button onClick={onLogin} variant="outline">
                  Login (Demo)
                </Button>
                <Link to="/join">
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="nav-join-btn">
                    Join Now
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-gray-700 hover:text-emerald-600 transition-colors font-medium px-4 py-2 ${
                    location.pathname === link.to ? 'text-emerald-600 bg-emerald-50' : ''
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4">
                <Link to="/join">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    Join Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Footer component
function Footer() {
  return (
    <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="main-footer">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="mb-4">
              <TravelodgesLogo variant="footer" />
            </div>
            <p className="text-gray-400">
              India's premier networking platform for independent resorts.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Platform</h4>
            <ul className="space-y-2">
              <li><Link to="/how-it-works" className="text-gray-400 hover:text-emerald-500">How It Works</Link></li>
              <li><Link to="/for-resorts" className="text-gray-400 hover:text-emerald-500">For Resorts</Link></li>
              <li><Link to="/properties" className="text-gray-400 hover:text-emerald-500">Properties</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-emerald-500">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-400 hover:text-emerald-500">About Us</Link></li>
              <li><Link to="/join" className="text-gray-400 hover:text-emerald-500">Join Us</Link></li>
              <li><a href="#" className="text-gray-400 hover:text-emerald-500">Blog</a></li>
              <li><a href="#" className="text-gray-400 hover:text-emerald-500">Careers</a></li>
              <li><Link to="/admin/login" className="text-gray-400 hover:text-emerald-500">Admin</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Email: travelodgesnetwork@gmail.com</li>
              <li>Phone: +91 8499092521</li>
              <li>Hyderabad, India</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 Travelodges. All rights reserved. Empowering independent resorts across India.</p>
        </div>
      </div>
    </footer>
  );
}

// Layout component
function Layout({ children, isLoggedIn, currentUser, onLogin, onLogout, onPlanChange }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation 
        isLoggedIn={isLoggedIn}
        currentUser={currentUser}
        onLogin={onLogin}
        onLogout={onLogout}
        onPlanChange={onPlanChange}
      />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
}

// SEO Helmet component
function SEOHelmet({ title, description, keywords }) {
  useEffect(() => {
    document.title = title || 'Travelodges - India\'s Premier Resort Networking Platform';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || 'Join 500+ independent resorts across India. Grow your direct bookings through cross-promotion without OTA commissions.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords || 'resort networking, independent resorts India, boutique hotels India, eco lodges, homestays, resort booking, travel India, offbeat India');
    }
  }, [title, description, keywords]);

  return null;
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  const handleLogin = () => {
    // Simulated login - in production, this would authenticate with backend
    const demoUser = {
      id: 'demo-resort-123',
      name: 'Paradise Beach Resort',
      email: 'paradise@resort.com',
      phone: '+91 9876543210',
      location: 'Goa',
      state: 'Goa',
      subscriptionPlan: 'Premium' // Default to Premium for demo
    };
    setCurrentUser(demoUser);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsLoggedIn(false);
  };

  const handlePlanChange = (newPlan) => {
    setCurrentUser(prev => ({ ...prev, subscriptionPlan: newPlan }));
  };

  const handleAdminLogin = (admin) => {
    setAdminUser(admin);
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
  };

  return (
    <BrowserRouter>
      <Layout 
        isLoggedIn={isLoggedIn}
        currentUser={currentUser}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onPlanChange={handlePlanChange}
      >
        <Routes>
          <Route 
            path="/" 
            element={
              <>
                <SEOHelmet 
                  title="Travelodges - Where Independent Resorts Grow Together"
                  description="India's premier cross-promotion platform for independent resorts. Increase bookings by 45% without OTA commissions. Join 500+ partner resorts."
                  keywords="independent resorts India, resort networking, no OTA commission, direct bookings, boutique hotels India, eco lodges, homestays India"
                />
                <HomePage />
              </>
            } 
          />
          <Route 
            path="/how-it-works" 
            element={
              <>
                <SEOHelmet 
                  title="How Travelodges Works - 4 Simple Steps"
                  description="Learn how to grow your resort bookings through our collaborative network. Simple 4-step process to increase direct bookings without commissions."
                  keywords="how resort networking works, increase resort bookings, direct bookings process, resort referral network"
                />
                <HowItWorksPage />
              </>
            } 
          />
          <Route 
            path="/for-resorts" 
            element={
              <>
                <SEOHelmet 
                  title="For Resort Owners - Grow Without OTA Commissions"
                  description="Join 500+ resort owners growing their business. 45% average booking increase, zero commission model, simple monthly subscription."
                  keywords="resort owners India, grow resort business, OTA alternative, direct bookings for resorts, resort management platform"
                />
                <ForResortsPage />
              </>
            } 
          />
          <Route 
            path="/properties" 
            element={
              <>
                <SEOHelmet 
                  title="Discover Partner Resorts - Unique Stays Across India"
                  description="Explore 500+ verified independent resorts, boutique hotels, eco lodges, and homestays across India. From wildlife resorts to beach retreats."
                  keywords="resorts India, boutique hotels India, eco lodges India, homestays India, unique stays India, offbeat India, wildlife resorts, beach resorts"
                />
                <PropertiesPage />
              </>
            } 
          />
          <Route 
            path="/pricing" 
            element={
              <>
                <SEOHelmet 
                  title="Pricing Plans - Simple Monthly Subscription"
                  description="Transparent pricing starting at ₹999/month. No setup fees, no commission. Basic, Pro, and Premium plans for resorts of all sizes."
                  keywords="resort pricing plans, monthly subscription, no commission booking platform, affordable resort marketing"
                />
                <PricingPage />
              </>
            } 
          />
          <Route 
            path="/about" 
            element={
              <>
                <SEOHelmet 
                  title="About Travelodges - Our Mission & Vision"
                  description="Supporting independent resorts across India through collaboration. Learn about our mission to promote offbeat India and help resorts grow together."
                  keywords="about Travelodges, resort networking platform, independent resort support, offbeat India tourism"
                />
                <AboutPage />
              </>
            } 
          />
          <Route 
            path="/join" 
            element={
              <>
                <SEOHelmet 
                  title="Join Travelodges - List Your Property"
                  description="Start your 30-day free trial. List your resort and begin receiving quality referrals from 500+ partner resorts across India."
                  keywords="list resort India, join resort network, resort onboarding, partner with Travelodges, grow resort business"
                />
                <JoinPage />
              </>
            } 
          />
          <Route 
            path="/payment-instructions" 
            element={
              <>
                <SEOHelmet 
                  title="Payment Instructions - Travelodges"
                  description="Complete your payment to activate your Travelodges membership and start growing your resort business."
                />
                <PaymentInstructionsPage />
              </>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <>
                <SEOHelmet 
                  title="Dashboard - Off-Season Collaboration Network"
                  description="Manage your off-season listings and connect with other resorts on Travelodges."
                />
                <DashboardPage currentUser={currentUser} />
              </>
            } 
          />
          <Route 
            path="/admin/login" 
            element={
              <>
                <SEOHelmet 
                  title="Admin Login - Travelodges"
                  description="Admin access to Travelodges management dashboard"
                />
                <AdminLoginPage onAdminLogin={handleAdminLogin} />
              </>
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              <>
                <SEOHelmet 
                  title="Admin Dashboard - Travelodges"
                  description="Manage resorts and platform settings"
                />
                <AdminDashboardPage adminUser={adminUser} onAdminLogout={handleAdminLogout} />
              </>
            } 
          />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;